import React from 'react';
import { Box } from '@mui/material';
import { SidControlCodeData } from 'common/types/sid-control-code-data';
import { SidFormData } from 'common/types/sid-form-data';
import { t } from 'i18next';
import styles from '../../AuthMainForm.module.css';
import { InlineNotification, SidControlCode } from '../../../../../../components';
import { DeviceLinkBox } from '../../../../../../components/DeviceLinkBox/DeviceLinkBox';
import { PersonalCodeWrapper } from '../../../../../../components/PersonalCodeWrapper/PersonalCodeWrapper';
import { SmartIdSubForm } from '../../../SmartIdSubForm/SmartIdSubForm';
import { BackToServiceProviderLink } from '../../../BackToServiceProviderLink/BackToServiceProviderLink';
import { AuthErrorResponseCode } from '../../../../../../common/types/auth-error-response';

interface SidTabPanelProps {
  sidFlows: string[];
  isMobile: boolean;
  authCountryCode: string;
  isDeviceLinkActive: boolean;
  setIsDeviceLinkActive: (active: boolean) => void;
  sidControlCodeData: SidControlCodeData;
  setSidControlCodeData: (data: SidControlCodeData) => void;
  displayingSidQrCode: boolean;
  setDisplayingSidQrCode: (displaying: boolean) => void;
  socketErrorCode: string;
  setSocketErrorCode: (code: string) => void;
  sidErrorOrigin: 'qr' | 'personal' | null;
  setSidErrorOrigin: (origin: 'qr' | 'personal' | null) => void;
  httpResponseOk: boolean;
  deviceLinkBoxRef: React.RefObject<HTMLDivElement | null>;
  notificationBoxRef: React.RefObject<HTMLDivElement | null>;
  notificationContinueBtnRef: React.RefObject<HTMLButtonElement | null>;
  initialSidControlCodeData: SidControlCodeData;
}

export const SidTabPanel = ({
                              sidFlows,
                              isMobile,
                              authCountryCode,
                              isDeviceLinkActive,
                              setIsDeviceLinkActive,
                              sidControlCodeData,
                              setSidControlCodeData,
                              displayingSidQrCode,
                              setDisplayingSidQrCode,
                              socketErrorCode,
                              setSocketErrorCode,
                              sidErrorOrigin,
                              setSidErrorOrigin,
                              httpResponseOk,
                              deviceLinkBoxRef,
                              notificationBoxRef,
                              notificationContinueBtnRef,
                              initialSidControlCodeData,
                            }: SidTabPanelProps) => {
  const hasDeviceLink = sidFlows.includes('device-link');
  const hasNotification = sidFlows.includes('notification');
  const showSidControlCode = !!(sidControlCodeData.authData || sidControlCodeData.deviceLink);

  if (displayingSidQrCode) {
    return (
      <SidControlCode
        controlCodeData={sidControlCodeData}
        displayQrCode={true}
        closeControlCode={() => {
          setSidControlCodeData(initialSidControlCodeData);
          setDisplayingSidQrCode(false);
        }}
        setSocketErrorCode={setSocketErrorCode}
        setSidErrorOrigin={setSidErrorOrigin}
      />
    );
  }

  return (
    <>
      {hasDeviceLink && (
        <Box role="tabpanel" sx={{
          mb: '1.15rem'
        }}>
          <DeviceLinkBox
            ref={deviceLinkBoxRef}
            isActive={isDeviceLinkActive}
            isMobile={isMobile}
            onStart={() => {
              if (!httpResponseOk) {
                setSocketErrorCode('INTERNAL_ERROR');
                return;
              }
              setSocketErrorCode('');
              setSidErrorOrigin(null);
              setSidControlCodeData({ deviceLink: { authCountryCode }, authData: null });
              setDisplayingSidQrCode(!isMobile);
            }}
            onActivate={() => setIsDeviceLinkActive(true)}
            notice={
              socketErrorCode && socketErrorCode !== 'INTERNAL_ERROR' && sidErrorOrigin === 'qr' ? (
                <InlineNotification appearance="error">
                  <span>{t(`form.socketError.${socketErrorCode}.text`)}</span>
                </InlineNotification>
              ) : null
            }
          />
        </Box>
      )}

      {hasNotification && !sidControlCodeData.deviceLink && (
        <Box className={styles.tightTop} style={{ scrollMarginTop: '80px' }} role="tabpanel" sx={{
          mb: '0.15rem'
        }}>
          <PersonalCodeWrapper
            ref={notificationBoxRef}
            isActive={!isDeviceLinkActive}
            onActivate={() => setIsDeviceLinkActive(false)}
            title={
              hasNotification && !hasDeviceLink
                ? t('form.sid.DeviceLink.alternativeSingle')
                : t('form.sid.DeviceLink.alternative')
            }
          >
            <SmartIdSubForm
              authCountryCode={authCountryCode}
              onSubmitSuccess={(formData: SidFormData) => {
                if (!httpResponseOk) {
                  setSocketErrorCode('INTERNAL_ERROR');
                  return;
                }
                setSidErrorOrigin(null);
                setSidControlCodeData({ authData: { ...formData, authCountryCode } });
              }}
              socketErrorCode={socketErrorCode}
              setSocketErrorCode={setSocketErrorCode}
              setIsDeviceLinkActive={setIsDeviceLinkActive}
              sidErrorOrigin={sidErrorOrigin}
              continueButtonRef={notificationContinueBtnRef}
            />
          </PersonalCodeWrapper>
        </Box>
      )}

      {showSidControlCode && (
        <SidControlCode
          controlCodeData={sidControlCodeData}
          displayQrCode={false}
          closeControlCode={() => {
            setSidControlCodeData(initialSidControlCodeData);
            setDisplayingSidQrCode(false);
          }}
          setSocketErrorCode={setSocketErrorCode}
          setSidErrorOrigin={setSidErrorOrigin}
        />
      )}
      <Box
        sx={{
          mt: '1.5rem',
          textAlign: 'center'
        }}>
        <BackToServiceProviderLink errorCode={AuthErrorResponseCode.USER_CANCEL} />
      </Box>
    </>
  );
};
